/**
 * Per-source data freshness tracking.
 *
 * Compares each source's last fetch time to its expected update interval.
 * Levels:
 *   Fresh    — last fetch < 1x interval
 *   Warning  — last fetch 1-2x interval
 *   Stale    — last fetch 2-4x interval
 *   Dead     — last fetch > 4x interval
 *   Disabled — source is explicitly disabled
 *
 * Emits 'source:status' events when freshness level changes.
 * Used by service-status-panel and signal-indicator.
 */

import { sourceManager } from '@/sources/source-manager';
import { SOURCE_REGISTRY } from '@/config/sources';
import { eventBus } from '@/core/events';

export type FreshnessLevel = 'fresh' | 'warning' | 'stale' | 'dead' | 'disabled';

export interface SourceFreshness {
  sourceId: string;
  sourceName: string;
  level: FreshnessLevel;
  lastFetchMs: number | null;
  expectedIntervalMs: number;
  /** How many intervals overdue (0 = fresh, 1.5 = 50% overdue, etc.) */
  overdueRatio: number;
  eventCount: number;
  lastError: string | null;
}

/**
 * Classify freshness based on how many intervals have elapsed since last fetch.
 */
function classifyFreshness(timeSinceLastFetchMs: number, expectedIntervalMs: number): FreshnessLevel {
  const ratio = timeSinceLastFetchMs / expectedIntervalMs;
  if (ratio < 1) return 'fresh';
  if (ratio < 2) return 'warning';
  if (ratio < 4) return 'stale';
  return 'dead';
}

/**
 * Get freshness status for all registered sources.
 */
export function getAllSourceFreshness(): SourceFreshness[] {
  const now = Date.now();
  const results: SourceFreshness[] = [];

  const registeredSources = sourceManager.getAll();
  const statusMap = sourceManager.getStatus();

  for (const sourceDef of SOURCE_REGISTRY) {
    const source = registeredSources.find(s => s.id === sourceDef.id);
    const status = statusMap.get(sourceDef.id);

    if (status?.state === 'disabled') {
      results.push({
        sourceId: sourceDef.id,
        sourceName: sourceDef.name,
        level: 'disabled',
        lastFetchMs: null,
        expectedIntervalMs: sourceDef.updateIntervalMs,
        overdueRatio: 0,
        eventCount: 0,
        lastError: null,
      });
      continue;
    }

    const lastFetchMs = status?.lastFetch
      ? new Date(status.lastFetch).getTime()
      : null;

    let level: FreshnessLevel;
    let overdueRatio: number;

    if (lastFetchMs === null) {
      // Never fetched — treat as dead unless source is new (not yet started)
      if (source) {
        level = 'dead';
        overdueRatio = Infinity;
      } else {
        level = 'disabled';
        overdueRatio = 0;
      }
    } else {
      const elapsed = now - lastFetchMs;
      level = classifyFreshness(elapsed, sourceDef.updateIntervalMs);
      overdueRatio = elapsed / sourceDef.updateIntervalMs;
    }

    results.push({
      sourceId: sourceDef.id,
      sourceName: sourceDef.name,
      level,
      lastFetchMs,
      expectedIntervalMs: sourceDef.updateIntervalMs,
      overdueRatio,
      eventCount: status?.eventCount ?? 0,
      lastError: status?.lastError ?? null,
    });
  }

  return results;
}

/**
 * Get freshness for a single source by ID.
 */
export function getSourceFreshness(sourceId: string): SourceFreshness | null {
  const all = getAllSourceFreshness();
  return all.find(s => s.sourceId === sourceId) ?? null;
}

/**
 * Get aggregate freshness summary — counts per level.
 */
export function getFreshnessSummary(): Record<FreshnessLevel, number> {
  const all = getAllSourceFreshness();
  const summary: Record<FreshnessLevel, number> = {
    fresh: 0,
    warning: 0,
    stale: 0,
    dead: 0,
    disabled: 0,
  };
  for (const s of all) {
    summary[s.level]++;
  }
  return summary;
}

/**
 * Start a periodic freshness check that emits source:status events
 * when freshness levels change. Returns a cleanup function.
 *
 * @param intervalMs Check interval in ms (default: 30_000)
 */
export function startFreshnessMonitor(intervalMs = 30_000): () => void {
  const previousLevels = new Map<string, FreshnessLevel>();

  function check(): void {
    const freshness = getAllSourceFreshness();

    for (const entry of freshness) {
      const prev = previousLevels.get(entry.sourceId);
      if (prev !== entry.level) {
        previousLevels.set(entry.sourceId, entry.level);
        // Only emit after the first round (skip initial population)
        if (prev !== undefined) {
          eventBus.emit('source:status', {
            sourceId: entry.sourceId,
            state: entry.level,
          });
        }
      }
    }
  }

  // Initial population
  check();

  const timer = setInterval(check, intervalMs);

  return () => {
    clearInterval(timer);
    previousLevels.clear();
  };
}

/** CSS color for a freshness level (uses theme variables). */
export function getFreshnessColor(level: FreshnessLevel): string {
  switch (level) {
    case 'fresh': return 'var(--accent-green)';
    case 'warning': return 'var(--accent-amber)';
    case 'stale': return 'var(--accent-red)';
    case 'dead': return 'var(--severity-critical)';
    case 'disabled': return 'var(--text-muted)';
  }
}

/** Human-readable label for a freshness level. */
export function getFreshnessLabel(level: FreshnessLevel): string {
  switch (level) {
    case 'fresh': return 'Fresh';
    case 'warning': return 'Warning';
    case 'stale': return 'Stale';
    case 'dead': return 'Dead';
    case 'disabled': return 'Disabled';
  }
}
